// node_modules
import React, { useState, useEffect, useContext, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { useSelector, useDispatch } from "react-redux";

// redux
import { useGetDiningOptionListQuery } from "../../redux/services/diningOption";
import { useGetStationOptionsListQuery } from "../../redux/services/foodStationOptions";
import { useCreateOrderMutation } from "../../redux/services/order";
import {
  useGetOrganizationNotificationOptionsCustomerQuery
} from '../../redux/services/organization'
import { setLoading } from "../../redux/slices/app.slice";

// primereact components
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { InputTextarea } from "primereact/inputtextarea";
import { MultiSelect } from "primereact/multiselect";
import { InputMask } from "primereact/inputmask";

// components
import Confetti from "../../components/Confetti";

// context
import { ToastContext } from "../../App";

// utils
import getSubdomain from "../../utils/getSubdomain";
import {
  getSubscription,
  requestPermission,
  subscribe,
  unsubscribe,
} from "../../utils/notification";
import { formatOrderRestriction } from "../../utils/convertStringToMinutes";

// styles
import * as styles from "./OrderPage.module.css";

// assets
import NoAllergies from "../../assets/no_allergy.svg";
import EggsAllergy from "../../assets/egg_allergy.svg";
import MilkAndDiaryAllergy from "../../assets/milk_dairy_allergy.svg";
import PeanutsAllergy from "../../assets/peanuts_allergy.svg";
import SeafoodAllergy from "../../assets/seafood_allergy.svg";
import SesameAllergy from "../../assets/sesame_seeds_allergy.svg";
import ShellfishAllergy from "../../assets/shellfish_allergy.svg";
import SoyAllergy from "../../assets/soy_allergy.svg";
import TreeNutsAllergy from "../../assets/tree_nuts_allergy.svg";
import WheatGlutenAllergy from "../../assets/wheat_gluten_allergy.svg";

import NoImg from "../../assets/no_img.svg";

const allergiesMapper = {
  none: NoAllergies,
  egg: EggsAllergy,
  "milk and dairy": MilkAndDiaryAllergy,
  peanuts: PeanutsAllergy,
  seafood: SeafoodAllergy,
  sesame: SesameAllergy,
  shellfish: ShellfishAllergy,
  soy: SoyAllergy,
  "tree nuts": TreeNutsAllergy,
  "wheat-gluten": WheatGlutenAllergy,
};

const OrderPage = ({ sendViaWS, stationChanged, setStationChanged }) => {
  const { id: stationId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const installPrompt = useRef(null);

  const showToast = useContext(ToastContext);

  const [showMobilePushInfo, setShowMobilePushInfo] = useState(false);

  const vapidKey = useSelector((state) => state.app.vapidKey);

  const uuid = localStorage.getItem("uuid");

  const {
    control,
    handleSubmit,
    watch,
    getValues,
    setValue,
    formState: { errors },
  } = useForm({
    mode: "onChange",
  });

  const notificationType = watch("notification");

  const openedStations = useSelector((state) => state.stations.openedStations);

  const {
    data: options,
    isSuccess: isGetDiningOptionsSuccess,
    isError: isGetDiningOptionsError,
  } = useGetDiningOptionListQuery({ id: stationId, subDomain: getSubdomain() });
  
  const {
    data: notifications,
    isSuccess: isGetNotificationsSuccess,
    isError: isGetNotificationsError
  } = useGetOrganizationNotificationOptionsCustomerQuery(getSubdomain());
  
  const currentStation = openedStations?.find(
    (station) => +station.id === +stationId
  );

  const {
    data: stationOptions,
    isSuccess: isGetStationOptionsSuccess,
    isError: isGetStationOptionsError,
  } = useGetStationOptionsListQuery({
    id: stationId,
    subDomain: getSubdomain(),
  });
  const [
    createOrder,
    {
      isSuccess: isCreateOrderSuccess,
      isError: isCreateOrderError,
      error: orderError,
    },
  ] = useCreateOrderMutation();

  useEffect(() => {
    window.addEventListener("beforeinstallprompt", (event) => {
      event.preventDefault();
      installPrompt.current = event;
    });
    dispatch(setLoading(true));
  }, []);

  useEffect(() => {
    if (isGetStationOptionsSuccess && isGetDiningOptionsSuccess) {
      dispatch(setLoading(false));
    }
  }, [isGetDiningOptionsSuccess, isGetStationOptionsSuccess]);

  useEffect(() => {
    if (stationChanged) {
      setValue("dining_option", undefined);

      for (const option of stationOptions) {
        setValue(`menu_option-${option.group.group_name}`, undefined);
      }

      setStationChanged(false);
    }
  }, [stationChanged]);

  useEffect(() => {
    setValue("email", "");
    setValue("phone", "");
  }, [notificationType]);

  useEffect(() => {
    if (isCreateOrderSuccess) {
      const uuid = localStorage.getItem("uuid");
      if (uuid) {
        sendViaWS({
          type: "order-status",
          data: { customer_id: uuid },
        });
      }

      setTimeout(() => {
        navigate("/");
      }, 4000);
    }
  }, [isCreateOrderSuccess]);

  useEffect(() => {
    if (isCreateOrderError) {
      if (orderError.data.errors?.length) {
        orderError.data.errors.forEach((error) => {
          if (error.param.startsWith("/")) {
            showToast(
              "error",
              orderError.status,
              error.param + " " + error.message
            );
          } else {
            showToast("error", orderError.status, error.message);
          }
        });
      } else {
        showToast("error", orderError.status, orderError.data.message);
      }
    }
  }, [isCreateOrderError]);
  
  useEffect(() => {
    if (isGetDiningOptionsError) {
      showToast("error", "Dining option", "Unable to load dining options");
    }
  }, [isGetDiningOptionsError])
  
  useEffect(() => {
    if (isGetNotificationsError) {
      showToast("error", "Notification", "Unable to load notification options");
    }
  }, [isGetNotificationsError])
  
  useEffect(()=> {
    if (isGetStationOptionsError) {
      showToast("error", "Station option", "Unable to load station options");
    }
  }, [isGetStationOptionsError])

  useEffect(() => {
    if (notificationType === "push") {
      if (
        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
          navigator.userAgent
        )
      ) {
        if (window.matchMedia("(display-mode: standalone)").matches) {
          requestPermission().then((a) => {
            if (a === "granted") {
              if (!vapidKey) {
                sendViaWS({
                  type: "get-vapid-key",
                });
              }
            } else {
              showToast(
                "error",
                "Error!",
                "Push notifications is disabled or app opened in Incognito Mode"
              );
              setValue("notification", "");
            }
          });
        } else {
          setShowMobilePushInfo(true);
          setValue("notification", "");
        }
      } else {
        if ("Notification" in window && Notification.permission === "denied") {
          requestPermission().then((a) => {
            if (a === "denied") {
              showToast(
                "error",
                "Error!",
                "Push notifications is disabled or app opened in Incognito Mode"
              );
              setValue("notification", "");
            }
          });
        }
        if ("Notification" in window && Notification.permission !== "denied") {
          requestPermission().then((a) => {
            if (a === "granted") {
              if (!vapidKey) {
                sendViaWS({
                  type: "get-vapid-key",
                });
              }
            } else {
              showToast(
                "error",
                "Error!",
                "Push notifications is disabled or app opened in Incognito Mode"
              );
              setValue("notification", "");
            }
          });
        }
      }
    } else if (notificationType === "email" || notificationType === "sms") {
      setShowMobilePushInfo(false);
      unsubscribe();
    }
  }, [vapidKey, notificationType]);

  const installPWA = async () => {
    try {
      await installPrompt.current.prompt();
    } catch (error) {
      showToast("error", "Error", "Can't install PWA");
    }
  };

  const onCreate = async (data) => {
    const payload = {
      customer_id: uuid,
      subdomain: getSubdomain(),
      food_station_id: +stationId,
      dining_option_id: data.dining_option,
      name: data.name,
      surname: data.surname,
      special_note: data.notes,
      options_id: [],
      allergies: data.allergies,
      notification: {},
    };

    if (data.notification === "push") {
      await subscribe(vapidKey);

      const subRes = await getSubscription();

      const sub = subRes.toJSON();

      payload.notification = {
        type: "push",
        auth: sub.keys.auth,
        p256dh: sub.keys.p256dh,
        endpoint: sub.endpoint,
      };
    }

    if (data.notification === "sms") {
      payload.notification = {
        type: "sms",
        phone: `+1${data.phone}`,
      };
    }

    if (data.notification === "email") {
      payload.notification = {
        type: "email",
        email: data.email,
      };
    }

    const menuOptionKeys = Object.keys(data).filter((key) =>
      key.startsWith("menu_option-")
    );

    menuOptionKeys.forEach((key) => {
      if (Array.isArray(data[key])) {
        data[key].forEach((item) => {
          payload.options_id.push(item);
        });
      } else {
        payload.options_id.push(data[key]);
      }
    });

    createOrder(payload);
  };

  const contactFieldsRenderer = () => {
    switch (notificationType) {
      case "email":
        return (
          <Controller
            name="email"
            control={control}
            rules={{
              required: "Enter your email",
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: "Email is invalid",
              },
            }}
            render={({ field, fieldState }) => (
              <div className="field">
                <label className={styles.label}>Email *</label>
                <div className={styles.inputWrapper}>
                  <InputText
                    className={`w-full ${styles.input} ${
                      fieldState.invalid ? "p-invalid" : ""
                    }`}
                    placeholder="Enter Your Email"
                    {...field}
                  />
                  {fieldState.error && (
                    <small className={`p-error `}>
                      {fieldState.error.message}
                    </small>
                  )}
                </div>
              </div>
            )}
          />
        );
      case "sms":
        return (
          <Controller
            name="phone"
            control={control}
            rules={{
              required: "Enter your phone number",
              minLength: {
                value: 10,
                message: "Phone number is invalid",
              },
            }}
            render={({ field, fieldState }) => (
              <div className="field">
                <label className={styles.label}>Phone Number *</label>
                <div className={styles.inputWrapper}>
                  <div className="p-inputgroup flex-1">
                    <span
                      className={`p-inputgroup-addon ${styles.phonePrefix}`}
                    >
                      +1
                    </span>
                    <InputMask
                      className={`w-full ${styles.input} ${styles.phoneInput} ${
                        fieldState.invalid ? "p-invalid" : ""
                      }`}
                      placeholder="(999) 999-9999"
                      prefix="+1"
                      mask="(999) 999-9999"
                      unmask={true}
                      {...field}
                    />
                  </div>

                  {fieldState.error && (
                    <small className={`p-error `}>
                      {fieldState.error.message}
                    </small>
                  )}
                  <p className={styles.phoneNote}>
                    <b>⚠️ NOTE!</b> if you choose <b>“Text message”</b>, you are{" "}
                    <b>opting in and giving consent</b> to receive text messages
                    regarding your order and when your order is ready for
                    pickup. <b>Msg & Data Rates May Apply.⚠️ </b>
                  </p>
                </div>
              </div>
            )}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      {currentStation ? (
        <div className={styles.pageWrapper}>
          <>
            <div className={styles.orderCard}>
              <div
                style={{
                  backgroundImage: `${currentStation.image_name ? `url(https://dwtl.food/public/images/${
                    currentStation?.image_name || ""
                  })` : ''}`,
                }}
                className={styles.stationImage}
              >
                <button
                  className={`${styles.backButton}`}
                  onClick={() => navigate("/")}
                >
                  <i className={`pi pi-angle-left `}></i>
                </button>

                <div className={styles.estimatedTime}>
                  {currentStation?.statistics.estimated_time
                    ? `~ ${currentStation?.statistics.estimated_time} wait`
                    : `~ 6 min wait`}
                </div>
              </div>
              <div className={styles.formWrapper}>
                {!isCreateOrderSuccess ? (
                  <>
                    {currentStation?.order_restriction &&
                      currentStation?.order_restriction !== "00:00" && (
                        <p className={styles.orderRestrictions}>
                          You can make one order per every{" "}
                          {formatOrderRestriction(
                            currentStation?.order_restriction
                          )}
                          .
                        </p>
                      )}
                    <h1>{`CREATE ${currentStation?.name || ""} ORDER`}</h1>
                    <form onSubmit={handleSubmit(onCreate)}>
                      <div className="field">
                        <label className={styles.label}>Name *</label>

                        <div className={styles.nameWrapper}>
                          <Controller
                            name="name"
                            control={control}
                            rules={{
                              required: "Enter your first name",
                              maxLength: {
                                value: 256,
                                message:
                                  "Name should be shorter than 256 characters",
                              },
                            }}
                            render={({ field, fieldState }) => (
                              <div className={styles.inputWrapper}>
                                <InputText
                                  className={`w-full ${styles.input} ${
                                    fieldState.invalid ? "p-invalid" : ""
                                  }`}
                                  placeholder="Enter Your First Name"
                                  {...field}
                                />
                                {fieldState.error && (
                                  <small className={`p-error `}>
                                    {fieldState.error.message}
                                  </small>
                                )}
                              </div>
                            )}
                          />

                          <Controller
                            name="surname"
                            control={control}
                            rules={{
                              required: "Enter your last name",
                              maxLength: {
                                value: 256,
                                message:
                                  "Last Name should be shorter than 256 characters",
                              },
                            }}
                            render={({ field, fieldState }) => (
                              <div className={styles.inputWrapper}>
                                <InputText
                                  className={`w-full ${styles.input} ${
                                    fieldState.invalid ? "p-invalid" : ""
                                  }`}
                                  placeholder="Enter Your Last Name"
                                  {...field}
                                />
                                {fieldState.error && (
                                  <small className={`p-error `}>
                                    {fieldState.error.message}
                                  </small>
                                )}
                              </div>
                            )}
                          />
                        </div>
                      </div>

                      {isGetDiningOptionsSuccess && (
                        <Controller
                          name="dining_option"
                          control={control}
                          rules={{ required: "Choose Dining Option" }}
                          render={({ field, fieldState }) => (
                            <div className="field">
                              <label className={styles.label}>
                                Please Choose Dining Option *
                              </label>
                              <div className={styles.dropDownWrapper}>
                                <Dropdown
                                  {...field}
                                  className={`${styles.withOutImage} ${
                                    fieldState.invalid ? "p-invalid" : ""
                                  }`}
                                  placeholder="Select a dining option"
                                  options={options}
                                  optionLabel="name"
                                  optionValue="id"
                                  panelClassName={`${styles.withOutImage} ${styles.dropDownPanel}`}
                                />
                                {fieldState.error && (
                                  <small className={`p-error `}>
                                    {fieldState.error.message}
                                  </small>
                                )}
                              </div>
                            </div>
                          )}
                        />
                      )}
                      
                      {isGetNotificationsSuccess && (
                        <Controller
                          name="notification"
                          control={control}
                          rules={{ required: "Choose Notification Option" }}
                          render={({ field, fieldState }) => (
                            <div className="field">
                              <label className={styles.label}>
                                Notification option *
                              </label>
                              <div className={styles.dropDownWrapper}>
                                <Dropdown
                                  {...field}
                                  placeholder="Select an option"
                                  optionLabel="label"
                                  optionValue="value"
                                  className={`${styles.withOutImage} ${
                                    fieldState.invalid ? "p-invalid" : ""
                                  }`}
                                  options={notifications.map(data => {
                                    return {
                                      value: data.title,
                                      label: data.title?.slice(0,1).toUpperCase()+data.title?.slice(1),
                                    }
                                  })}
                                  panelClassName={`${styles.withOutImage} ${styles.dropDownPanel}`}
                                />
                                {showMobilePushInfo && (
                                  <div className={styles.pushMobileInfoText}>
                                    In order to use Push notifications in mobile -
                                    please, install PWA first:
                                    <span
                                      onClick={installPWA}
                                      className={styles.installLink}
                                    >
                                    Install
                                  </span>
                                  </div>
                                )}
                                {fieldState.error && (
                                  <small className={`p-error `}>
                                    {fieldState.error.message}
                                  </small>
                                )}
                              </div>
                            </div>
                          )}
                        />
                      )}

                      {contactFieldsRenderer()}

                      <Controller
                        name="allergies"
                        control={control}
                        rules={{ required: "Choose Allergies" }}
                        render={({ field, fieldState }) => (
                          <div className="field">
                            <label className={styles.label}>Allergies *</label>
                            <br />
                            <div className={styles.multiSelectWrapper}>
                              <MultiSelect
                                {...field}
                                itemTemplate={(item) => {
                                  return (
                                    <div className={styles.listItem}>
                                      <div
                                        style={{
                                          backgroundImage: `url(${
                                            allergiesMapper[item.value]
                                          })`,
                                        }}
                                      ></div>
                                      {item.label}
                                    </div>
                                  );
                                }}
                                onChange={(e) => {
                                  if (
                                    field?.value?.length === 1 &&
                                    field?.value[0] === "none"
                                  ) {
                                    return field.onChange([
                                      e.selectedOption.value,
                                    ]);
                                  }

                                  if (e.selectedOption.value === "none") {
                                    return field.onChange([
                                      e.selectedOption.value,
                                    ]);
                                  }

                                  return field.onChange(e);
                                }}
                                options={[
                                  { label: "NONE", value: "none" },
                                  { label: "Egg", value: "egg" },
                                  {
                                    label: "Milk and Dairy",
                                    value: "milk and dairy",
                                  },
                                  { label: "Peanuts", value: "peanuts" },
                                  { label: "Seafood", value: "seafood" },
                                  { label: "Sesame", value: "sesame" },
                                  { label: "Shellfish", value: "shellfish" },
                                  { label: "Soy", value: "soy" },
                                  { label: "Tree Nuts", value: "tree nuts" },
                                  {
                                    label: "Wheat-Gluten",
                                    value: "wheat-gluten",
                                  },
                                ]}
                                optionLabel="label"
                                optionValue="value"
                                display="chip"
                                placeholder="Select an allergies"
                                className={`${styles.multiSelect} ${
                                  fieldState.invalid ? "p-invalid" : ""
                                }`}
                                panelClassName={`${styles.multiSelectPanel}`}
                              />
                              {fieldState.error && (
                                <small className={`p-error `}>
                                  {fieldState.error.message}
                                </small>
                              )}
                            </div>
                          </div>
                        )}
                      />

                      {isGetStationOptionsSuccess &&
                        stationOptions.map((option) => (
                          <Controller
                            name={`menu_option-${option.group.group_name}`}
                            control={control}
                            rules={{
                              required: `Choose ${option.group.group_name}`,
                              validate: (e) => {
                                if (e.length > option.group.max_multiple) {
                                  return `You can select only ${option.group.max_multiple} options`;
                                }
                              },
                            }}
                            render={({ field, fieldState }) => (
                              <div className="field">
                                <label className={styles.label}>
                                  {option.group.group_name} *{" "}
                                  {!!option.group.allow_multiple && (
                                    <span className={styles.maxOptionsLabel}>
                                      (max {option.group.max_multiple})
                                    </span>
                                  )}
                                </label>
                                <br />
                                {option.group.allow_multiple ? (
                                  <div className={styles.multiSelectWrapper}>
                                    <MultiSelect
                                      {...field}
                                      options={option.items}
                                      optionLabel="name"
                                      optionValue="id"
                                      display="chip"
                                      placeholder={`Select ${option.group.group_name}`}
                                      className={`${styles.multiSelect} ${
                                        fieldState.invalid ? "p-invalid" : ""
                                      }`}
                                      panelClassName={styles.multiSelectPanel}
                                      itemTemplate={(item) => {
                                        return (
                                          <div className={styles.listItem}>
                                            <div
                                              style={{
                                                background: `url(https://dwtl.food/public/images/${item?.image_name}), url(${NoImg})`,
                                                backgroundSize: "cover, cover",
                                              }}
                                            ></div>
                                            {item.name}
                                          </div>
                                        );
                                      }}
                                    />
                                    {fieldState.error && (
                                      <small className={`p-error `}>
                                        {fieldState.error.message}
                                      </small>
                                    )}
                                  </div>
                                ) : (
                                  <div className={styles.dropDownWrapper}>
                                    <Dropdown
                                      {...field}
                                      placeholder={`Select ${option.group.group_name}`}
                                      options={option.items}
                                      optionLabel="name"
                                      optionValue="id"
                                      panelClassName={styles.dropDownPanel}
                                      className={`${
                                        fieldState.invalid ? "p-invalid" : ""
                                      }`}
                                      itemTemplate={(item) => {
                                        return (
                                          <div className={styles.listItem}>
                                            <div
                                              style={{
                                                background: `url(https://dwtl.food/public/images/${item?.image_name}), url(${NoImg})`,
                                                backgroundSize: "cover, cover",
                                              }}
                                            ></div>
                                            {item.name}
                                          </div>
                                        );
                                      }}
                                    />
                                    {fieldState.error && (
                                      <small className={`p-error `}>
                                        {fieldState.error.message}
                                      </small>
                                    )}
                                  </div>
                                )}
                              </div>
                            )}
                          />
                        ))}

                      <Controller
                        name="notes"
                        control={control}
                        rules={{
                          maxLength: {
                            value: 100,
                            message: "Maximum 100 characters",
                          },
                        }}
                        render={({ field, fieldState }) => (
                          <div className="field mb-7">
                            <label className={styles.label}>
                              Special Notes
                            </label>
                            <InputTextarea
                              {...field}
                              value={field.value || ""}
                              onChange={(e) => field.onChange(e.target.value)}
                              onBlur={field.onBlur}
                              className={`w-full ${styles.textArea} ${
                                fieldState.invalid && "p-invalid"
                              }`}
                            />
                            {fieldState.error && (
                              <p className={styles.error}>
                                Notes limit is 100 characters
                              </p>
                            )}
                          </div>
                        )}
                      />

                      <Button
                        id="submitButton"
                        label="Submit Order"
                        type="submit"
                        className={styles.submitButton}
                      />
                    </form>
                  </>
                ) : (
                  <>
                    <div className={styles.orderCreated}>
                      Order confirmed, {getValues("name")}{" "}
                      {getValues("surname")}! The DWTL Culinary Team is firing
                      up the magic — we’ll notify you when your creation is
                      ready!
                    </div>
                    <Confetti />
                  </>
                )}
              </div>
            </div>
          </>
        </div>
      ) : (
        <div style={{ textAlign: "center", fontSize: "36px" }}>
          Station is closed
        </div>
      )}
    </>
  );
};

export default OrderPage;
